import {
	createSelector,
	createSlice,
	type PayloadAction,
} from '@reduxjs/toolkit';
import type {
	GoogleAdsGeoData,
	GoogleAdsPrimaryPurchaseContext,
	SanitizedStripeBillingDetails,
} from '@/analytics/googleAdsUserData';
import type { RootState } from '.';

interface GoogleAdsState {
	primaryPurchaseContext: GoogleAdsPrimaryPurchaseContext | null;
	geo: GoogleAdsGeoData | null;
}

const initialState: GoogleAdsState = {
	primaryPurchaseContext: null,
	geo: null,
};

const googleAdsSlice = createSlice({
	name: 'googleAds',
	initialState,
	reducers: {
		setGoogleAdsPrimaryPurchaseContext: (
			state,
			action: PayloadAction<GoogleAdsPrimaryPurchaseContext>
		) => {
			const stripeBillingDetails =
				action.payload.processor === 'stripe'
					? action.payload.stripeBillingDetails
					: null;

			state.primaryPurchaseContext = {
				...action.payload,
				stripeBillingDetails,
			};
		},
		setGoogleAdsGeo: (
			state,
			action: PayloadAction<GoogleAdsGeoData | null>
		) => {
			state.geo = action.payload;
		},
		clearGoogleAdsGeo: (state) => {
			state.geo = null;
		},
	},
});

export const {
	setGoogleAdsPrimaryPurchaseContext,
	setGoogleAdsGeo,
	clearGoogleAdsGeo,
} = googleAdsSlice.actions;

export const selectGoogleAdsPrimaryPurchaseContext = (state: any) =>
	state.googleAds.primaryPurchaseContext as GoogleAdsPrimaryPurchaseContext | null;

export const selectGoogleAdsStripeBillingDetailsForPurchase = (
	state: any,
	subscriptionId?: string | null
): SanitizedStripeBillingDetails | undefined => {
	const context = selectGoogleAdsPrimaryPurchaseContext(state);
	if (
		!subscriptionId ||
		context?.processor !== 'stripe' ||
		context.subscriptionId !== subscriptionId
	) {
		return undefined;
	}

	return context.stripeBillingDetails ?? undefined;
};

export const selectGoogleAdsGeo = createSelector(
	[
		(state: RootState) => state.signup.userLocation as string | null | undefined,
		(state: RootState) => state.googleAds.geo as GoogleAdsGeoData | null,
	],
	(userLocation, geo): GoogleAdsGeoData => ({
		country: geo?.country ?? userLocation,
		city: geo?.city,
		region: geo?.region,
	})
);

export default googleAdsSlice.reducer;
